import { IdcardOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { waitForTransaction } from '@wagmi/core';
import { Avatar, Badge, Button, Col, Descriptions, message, Result, Row, Space, Typography } from 'antd';
import { useAtom } from 'jotai';
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAccount, useContractWrite, usePrepareContractWrite, useWalletClient } from 'wagmi';

import contract from '@/contract';
import checkAdmin from '@/libs/checkAdmin';
import { isGotNftAtom, isGrantRoleAtom } from '@/store';
import { Role } from '@/types';

const { Title, Paragraph } = Typography;

function Login() {
  const { isConnected, address } = useAccount();
  const isAdminUser = checkAdmin(address);
  const [isGotNFT, setGotNFT] = useAtom(isGotNftAtom);
  const [isGrantRole, setGrantRole] = useAtom(isGrantRoleAtom);
  const [messageApi, contextHolder] = message.useMessage();
  const { data: walletClient } = useWalletClient();
  const [isPhase1Loading, setPhase1Loading] = useState(false);
  const [isPhase2Loading, setPhase2Loading] = useState(false);

  const isRegistered = isAdminUser || (isGotNFT && isGrantRole);

  const { config: mintConfig } = usePrepareContractWrite({
    ...contract,
    functionName: 'mint',
    walletClient,
    value: BigInt('1'),
    onError(error) {
      console.log('mintConfig Error', error);
    },
    onSuccess(data) {
      console.log('mintConfig Success', data);
    }
  });
  const { writeAsync: mintNft } = useContractWrite(mintConfig);

  const { config: createAccountConfig } = usePrepareContractWrite({
    ...contract,
    functionName: 'createAccount',
    walletClient,
    args: [1],
    onError(error) {
      console.log('createAccountConfig Error', error);
    },
    onSuccess(data) {
      console.log('createAccountConfig Success', data);
    }
  });
  const { writeAsync: createAccount } = useContractWrite(createAccountConfig);

  const { config: getRoleAConfig } = usePrepareContractWrite({
    ...contract,
    functionName: 'getRoleA',
    walletClient,
    args: [1],
    value: BigInt('10'),
    onError(error) {
      console.log('getRoleAConfig Error', error);
    },
    onSuccess(data) {
      console.log('getRoleAConfig Success', data);
    }
  });
  const { writeAsync: getRoleA } = useContractWrite(getRoleAConfig);

  const { config: getRoleBConfig } = usePrepareContractWrite({
    ...contract,
    functionName: 'getRoleB',
    walletClient,
    args: [1],
    value: BigInt('10'),
    onError(error) {
      console.log('getRoleBConfig Error', error);
    },
    onSuccess(data) {
      console.log('getRoleBConfig Success', data);
    }
  });
  const { writeAsync: getRoleB } = useContractWrite(getRoleBConfig);

  const { config: getRoleCConfig } = usePrepareContractWrite({
    ...contract,
    functionName: 'getRoleC',
    walletClient,
    args: [1],
    value: BigInt('10'),
    onError(error) {
      console.log('getRoleCConfig Error', error);
    },
    onSuccess(data) {
      console.log('getRoleCConfig Success', data);
    }
  });
  const { writeAsync: getRoleC } = useContractWrite(getRoleCConfig);

  const onBuyNft = useCallback(async () => {
    const key = 'onBuyNft';
    try {
      setPhase1Loading(true);
      console.log('mintNft', mintNft);
      if (mintNft) {
        messageApi.open({
          key,
          type: 'loading',
          content: 'Minting NFT...',
          duration: 0
        });
        const mintNftTxn = await mintNft();
        await waitForTransaction(mintNftTxn);
        messageApi.open({
          key,
          type: 'success',
          content: 'NFT minted success',
          duration: 2.5
        });
      }

      console.log('createAccount', createAccount);

      if (createAccount) {
        messageApi.open({
          key,
          type: 'loading',
          content: 'Create Account...',
          duration: 0
        });
        const createAccountTxn = await createAccount();
        const result = await waitForTransaction(createAccountTxn);
        console.log(result);
        messageApi.open({
          key,
          type: 'success',
          content: 'Account created success',
          duration: 2.5
        });
      }
      setGotNFT(true);
    } catch (error) {
      messageApi.open({
        key,
        type: 'error',
        content: 'Got Error'
      });
    } finally {
      setPhase1Loading(false);
    }
  }, [mintNft, createAccount]);

  const onGrantRole = useCallback(
    async (role: Role) => {
      const key = 'onGrantRole';
      console.log('getRoleA', getRoleA);
      console.log('getRoleB', getRoleB);
      console.log('getRoleC', getRoleC);

      try {
        setPhase2Loading(true);
        if (getRoleA && getRoleB && getRoleC) {
          messageApi.open({
            key,
            type: 'loading',
            content: 'Loading...',
            duration: 0
          });

          if (role === Role.ADMIN) {
            const getRoleTxn = await getRoleA();
            await waitForTransaction(getRoleTxn);
          } else if (role === Role.DEVELOPER) {
            const getRoleTxn = await getRoleB();
            await waitForTransaction(getRoleTxn);
          } else if (role == Role.CONTRIBUTOR) {
            const getRoleTxn = await getRoleC();
            await waitForTransaction(getRoleTxn);
          }

          messageApi.open({
            key,
            type: 'success',
            content: 'Finished!',
            duration: 2
          });
          setGrantRole(true);
        }
      } catch (error) {
        messageApi.open({
          key,
          type: 'error',
          content: 'Got Error',
          duration: 2
        });
      } finally {
        setPhase2Loading(false);
      }
    },
    [getRoleA, getRoleB, getRoleC]
  );

  let renderComponent = null;

  if (!isConnected) {
    renderComponent = (
      <Row justify="center" key="isConnected">
        <Col>
          <Space direction="vertical" align="center">
            <Title level={2}>Login & Sign up Here</Title>
            <ConnectButton />
          </Space>
        </Col>
      </Row>
    );
  } else if (isRegistered) {
    renderComponent = (
      <Row justify="center" key="isRegistered">
        <Col>
          <Space direction="vertical" align="center">
            <Result
              status="success"
              title="Login Success!"
              subTitle={address}
              extra={[
                <Space key="descriptions" direction="vertical" size={30}>
                  <Descriptions title="User Info" bordered>
                    <Descriptions.Item label="Role" span={3}>
                      {isAdminUser ? Role.ADMIN : Role.CONTRIBUTOR}
                    </Descriptions.Item>
                    <Descriptions.Item label="Status" span={3}>
                      <Badge status="success" text="Active" />
                    </Descriptions.Item>
                    <Descriptions.Item label="6551 NFT">
                      <Space direction="vertical">
                        <Avatar
                          shape="square"
                          size={150}
                          src={
                            isAdminUser
                              ? 'https://noun-api.com/beta/pfp?head=159&glasses=8&background=0&body=1&accessory=4'
                              : 'https://noun-api.com/beta/pfp?head=166&glasses=16&background=1&body=9&accessory=11'
                          }
                        />
                        0xbfACc5ffF51d5Da3C4B32cbc1189bF13bC37e5a7
                      </Space>
                    </Descriptions.Item>
                  </Descriptions>
                  <Space>
                    <Link to="/my-issues">
                      <Button type="primary">Go My Issues</Button>
                    </Link>
                    <Link to="/">
                      <Button>Go Issue List</Button>
                    </Link>
                  </Space>
                </Space>
              ]}
            />
          </Space>
        </Col>
      </Row>
    );
  } else if (!isGotNFT) {
    renderComponent = (
      <Row justify="center" key="isGotNFT">
        <Col>
          <Space direction="vertical" align="center">
            <Result
              icon={<UsergroupAddOutlined />}
              title="Sign up now"
              subTitle={address}
              extra={[
                <Paragraph key="text">
                  To become a member, you should buy our 6551 NFT as your account proof with 0.001 Matic
                </Paragraph>,
                <Space key="buttons">
                  <Button type="primary" disabled={isPhase1Loading} onClick={onBuyNft}>
                    Get 6551 NFT (0.001 Matic)
                  </Button>
                </Space>
              ]}
            />
          </Space>
        </Col>
      </Row>
    );
  } else if (!isGrantRole) {
    renderComponent = (
      <Row justify="center" key="isGrantRole">
        <Col>
          <Space direction="vertical" align="center">
            <Result
              icon={<IdcardOutlined />}
              title="Sign up now"
              subTitle={address}
              extra={[
                <Paragraph key="text">To become a member, you should get the role</Paragraph>,
                <Space key="buttons">
                  <Button type="primary" disabled={isPhase2Loading} onClick={() => onGrantRole(Role.ADMIN)}>
                    Get Admin Role
                  </Button>
                  <Button type="primary" disabled={isPhase2Loading} onClick={() => onGrantRole(Role.DEVELOPER)}>
                    Get Developer Role
                  </Button>
                  <Button type="primary" disabled={isPhase2Loading} onClick={() => onGrantRole(Role.CONTRIBUTOR)}>
                    Get Contributor Role
                  </Button>
                </Space>
              ]}
            />
          </Space>
        </Col>
      </Row>
    );
  }

  return (
    <div>
      {contextHolder}
      {renderComponent}
    </div>
  );
}

export default Login;
